import CheckIcon from '@mui/icons-material/Check';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { BENEFIT_PICTURES } from 'probonio-shared-ui/component/button';
import { BikeleasingIcon } from 'probonio-shared-ui/component/icon';
import { useTranslation } from 'react-i18next';
import { BackIconButton } from '../../component/button/BackIconButton';
import { MobileLayout } from '../../component/layout';
import { AppToolbar } from '../../component/layout/AppToolbar';
import { useBikeleasingDetails } from '../../module/benefit/bikeleasing/useBikeleasingDetails';
import { useBackNavigator } from '../../module/navigation/BackNavigator';

interface TextProps {
  children: React.ReactNode;
}

const InfoText: React.FC = () => {
  const { t } = useTranslation('benefitModule');
  return (
    <Paper data-test-id="bike-info-intro" elevation={0} sx={{ paddingY: 0.3, width: '100%', paddingRight: 0.3 }}>
      <Box sx={{ ml: 0.8 }}>
        <Typography variant="description">{t('benefit.BIKELEASING.infoPage.infoText')}</Typography>
      </Box>
    </Paper>
  );
};

const MoreAboutBenefit: React.FC = () => {
  const { t } = useTranslation('benefitModule');
  return (
    <Paper data-test-id="bike-info-intro" elevation={0} sx={{ paddingY: 0.3, width: '100%', paddingRight: 0.3 }}>
      <Box sx={{ ml: 0.8 }}>
        <Typography variant="descriptionBold">{t('benefit.BIKELEASING.infoPage.howItWorks.moreAbout')}</Typography>
        <Divider variant={'fullWidth'} sx={{ marginRight: 0.3, marginBottom: 0.3, marginTop: 0.1 }} />
        <ul>
          <li>
            <a target="_blank" href="https://www.youtube.com/watch?v=PLy7WGVNcFg" rel="noreferrer">
              How to Dienstrad
            </a>
          </li>
        </ul>
      </Box>
    </Paper>
  );
};

const DesiredBike: React.FC = () => {
  const { t } = useTranslation('benefitModule');
  return (
    <Paper data-test-id="bike-info-desired-bike" elevation={0} sx={{ paddingY: 0.3, width: '100%', paddingRight: 0.3 }}>
      <Box sx={{ ml: 0.8 }}>
        <Typography variant="description">{t('benefit.BIKELEASING.infoPage.desiredBike')}</Typography>
      </Box>
    </Paper>
  );
};

export const ReceiptAdvantagesText: React.FC<TextProps> = ({ children }) => {
  return (
    <Box
      sx={{
        marginLeft: 0.3,
        marginRight: 0.5,
        marginBottom: 0.7,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <CheckIcon color="success" />
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
};

const ReceiptBenefits: React.FC = () => {
  const { t } = useTranslation('benefitModule');
  const details = useBikeleasingDetails();

  return (
    <Paper data-test-id="bike-receipt-advantages" elevation={0} sx={{ paddingY: 0.3, width: '100%' }}>
      <Box sx={{ ml: 0.8 }}>
        <Typography variant="descriptionBold">{t('benefit.BIKELEASING.infoPage.howItWorks.name')}</Typography>
        <Divider variant={'fullWidth'} sx={{ marginRight: 0.3, marginBottom: 0.3, marginTop: 0.1 }} />
        <Typography
          variant={'description'}
          component="p"
          sx={{
            marginBottom: 0.7,
          }}
        >
          {t('benefit.BIKELEASING.infoPage.howItWorks.info')}
        </Typography>
        <ReceiptAdvantagesText>{t('benefit.BIKELEASING.infoPage.howItWorks.calculateCosts')}</ReceiptAdvantagesText>
        <ReceiptAdvantagesText>
          {t('benefit.BIKELEASING.infoPage.howItWorks.bikeLeasing', { companyCode: details?.bikeleasingCondition.companyCode })}
        </ReceiptAdvantagesText>
        <ReceiptAdvantagesText>{t('benefit.BIKELEASING.infoPage.howItWorks.retailPartners')}</ReceiptAdvantagesText>
        <ReceiptAdvantagesText>{t('benefit.BIKELEASING.infoPage.howItWorks.bikePickUp')}</ReceiptAdvantagesText>
        <ReceiptAdvantagesText>{t('benefit.BIKELEASING.infoPage.howItWorks.leasingContract')}</ReceiptAdvantagesText>
      </Box>
    </Paper>
  );
};

export const BikeleasingInfoPage: React.FC = () => {
  const backNavigator = useBackNavigator();
  const { t } = useTranslation('benefitModule');

  return (
    <MobileLayout
      header={<AppToolbar title={t('benefit.BIKELEASING.infoPage.header')} backButton={<BackIconButton onClick={backNavigator.back} />} />}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginX: 1,
          gap: 0.8,
          marginBottom: 0.7,
        }}
      >
        <Box
          data-test-id="bike-info-image"
          component="img"
          src={BENEFIT_PICTURES.BIKE}
          sx={{
            borderRadius: '8px',
            width: '100%',
            maxWidth: 410,
          }}
        />
        <InfoText />
        <DesiredBike />
        <ReceiptBenefits />
        <MoreAboutBenefit />
        <BikeleasingIcon width={'90%'} />
      </Box>
    </MobileLayout>
  );
};
